// src/pages/settings/DeepkiImportPanel.tsx — dropzone d'import Deepki pour un PDL (source==="deepki").
// Flux : drop → validation serveur (ts-ingest, en mémoire, rien stocké) → affichage du rapport →
// confirmation utilisateur → intégration (stocke le fichier tel quel + upsert des relevés),
// overlay d'attente pendant l'appel.

import { useRef, useState } from "react";
import LoadingScreen from "../../components/LoadingScreen";
import { useMinDuration } from "../../hooks/useMinDuration";
import type { PdlRecord } from "../../api/metersApi";
import { validateDeepkiFile, ingestDeepkiFile, type DeepkiValidationReport, type DeepkiImportRecord } from "../../api/deepkiApi";

const ACCEPTED = ".xlsx";

function fmtDate(epochS: number): string {
  return new Date(epochS * 1000).toISOString().substring(0, 10);
}

// Dernier dimanche d'un mois donné (1-12), en UTC — les timestamps du fichier Deepki sont des
// heures locales naïves parsées comme UTC (cf. server/ts-ingest/connectors/deepki/excel.ts),
// donc les champs UTC ci-dessous correspondent bien à l'heure murale d'origine.
function lastSundayOfMonth(year: number, month1to12: number): number {
  const d = new Date(Date.UTC(year, month1to12, 0)); // dernier jour du mois
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());       // recule jusqu'au dimanche précédent (ou reste si déjà dimanche)
  return d.getUTCDate();
}

// France : passage à l'heure d'été (dernier dimanche de mars, 2h→3h, heure "sautée") ou à
// l'heure d'hiver (dernier dimanche d'octobre, 3h→2h, heure dédoublée) — les deux tombent sur 2h.
function dstLabel(iso: string): string | null {
  const d = new Date(iso);
  if (d.getUTCHours() !== 2) return null;
  const y = d.getUTCFullYear(), mo = d.getUTCMonth() + 1, day = d.getUTCDate();
  if (mo === 3  && day === lastSundayOfMonth(y, 3))  return "changement d'heure été";
  if (mo === 10 && day === lastSundayOfMonth(y, 10)) return "changement d'heure hiver";
  return null;
}

function fmtGap(iso: string, pas: string): string {
  const d = new Date(iso);
  const date = d.toISOString().substring(0, 10);
  if (pas !== "heure") return date;
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const dst = dstLabel(iso);
  return `${date} ${hh}h${dst ? ` (${dst})` : ""}`;
}

// Regroupe les trous consécutifs (même écart que le pas détecté, même statut DST) en un seul
// segment — un mois entier de jours manquants ne doit pas s'afficher comme 30 entrées séparées.
function groupConsecutiveGaps(gaps: string[], pas: string): string[][] {
  const stepS = pas === "heure" ? 3600 : 86400;
  const groups: string[][] = [];
  let current: string[] = [];
  let prevTs: number | null = null;
  let prevDst: string | null = null;
  for (const iso of gaps) {
    const ts = Math.floor(new Date(iso).getTime() / 1000);
    const dst = pas === "heure" ? dstLabel(iso) : null;
    if (prevTs !== null && ts === prevTs + stepS && dst === prevDst) {
      current.push(iso);
    } else {
      if (current.length) groups.push(current);
      current = [iso];
    }
    prevTs = ts; prevDst = dst;
  }
  if (current.length) groups.push(current);
  return groups;
}

function fmtGapGroup(group: string[], pas: string): { text: string; dst: boolean } {
  if (group.length === 1) return { text: fmtGap(group[0], pas), dst: !!(pas === "heure" && dstLabel(group[0])) };
  const first = new Date(group[0]), last = new Date(group[group.length - 1]);
  const firstDate = first.toISOString().substring(0, 10), lastDate = last.toISOString().substring(0, 10);
  if (pas !== "heure") return { text: `${firstDate} → ${lastDate}`, dst: false };
  const hh = (d: Date) => `${String(d.getUTCHours()).padStart(2, "0")}h`;
  const text = firstDate === lastDate
    ? `${firstDate} ${hh(first)} → ${hh(last)}`
    : `${firstDate} ${hh(first)} → ${lastDate} ${hh(last)}`;
  return { text, dst: false };
}

export function DeepkiImportPanel({ pdl, siteId, lastImport, showToast, onImported }: {
  pdl:             PdlRecord;
  siteId:          string;
  lastImport:      DeepkiImportRecord | null;
  showToast:       (msg: string) => void;
  /** Reçoit l'import fraîchement intégré (tsFrom/tsTo…) — le parent en profite pour caler la plage "Visualiser" dessus. */
  onImported:      (report: DeepkiImportRecord) => void;
}) {
  const [drag,        setDrag]        = useState(false);
  const [filename,    setFilename]    = useState<string | null>(null);
  const [dataBase64,  setDataBase64]  = useState<string | null>(null);
  const [validating,  setValidating]  = useState(false);
  const [report,      setReport]      = useState<DeepkiValidationReport | null>(null);
  const [validateErr, setValidateErr] = useState<string | null>(null);
  const [ingesting,   setIngesting]   = useState(false);
  const ingestingDelayed = useMinDuration(ingesting);
  const [ingestErr,   setIngestErr]   = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);

  const reset = () => { setFilename(null); setDataBase64(null); setReport(null); setValidateErr(null); setIngestErr(null); };

  const handleFile = (file: File) => {
    reset();
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setValidateErr("Format non supporté. Utilisez un fichier .xlsx.");
      return;
    }
    setFilename(file.name);
    const r = new FileReader();
    r.onload = async () => {
      const dataUrl = r.result as string;
      const b64 = dataUrl.substring(dataUrl.indexOf(",") + 1);
      setDataBase64(b64);
      setValidating(true);
      try {
        const rep = await validateDeepkiFile({ pdlId: pdl.id, filename: file.name, dataBase64: b64 });
        setReport(rep);
      } catch (e) {
        setValidateErr((e as Error).message);
      } finally {
        setValidating(false);
      }
    };
    r.readAsDataURL(file);
  };

  const handleConfirm = async () => {
    if (!filename || !dataBase64) return;
    setIngesting(true); setIngestErr(null);
    try {
      const res = await ingestDeepkiFile({ siteId, pdlId: pdl.id, filename, dataBase64 });
      showToast(`Import Deepki intégré ✓ (${res.rowsIngested} relevés)`);
      reset();
      onImported(res.report);
    } catch (e) {
      setIngestErr((e as Error).message);
    } finally {
      setIngesting(false);
    }
  };

  return (
    <div style={{position:"relative",border:"1px solid #e8e8e8",borderRadius:8,padding:"14px 16px",marginBottom:16,background:"#fbfbfb"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8,flexWrap:"wrap",gap:6}}>
        <span style={{fontSize:12,fontWeight:700,fontFamily:"var(--ff-title)",color:"var(--text-primary)"}}>
          📥 Importer des données Deepki
        </span>
        {lastImport && (
          <span style={{fontSize:11,color:"var(--text-faint)"}}>
            Dernier import : {lastImport.rowCount} lignes ({lastImport.pas}), {fmtDate(lastImport.tsFrom)} → {fmtDate(lastImport.tsTo)},
            {" "}le {new Date(lastImport.createdAt).toLocaleString("fr-FR")}
          </span>
        )}
      </div>

      {!filename && (
        <div onClick={()=>ref.current?.click()}
          onDragOver={e=>{e.preventDefault();setDrag(true);}}
          onDragLeave={()=>setDrag(false)}
          onDrop={e=>{e.preventDefault();setDrag(false);const f=e.dataTransfer.files[0];if(f)handleFile(f);}}
          style={{border:`2px dashed ${drag?"var(--green-dim)":"#ddd"}`,borderRadius:8,cursor:"pointer",
            padding:"18px 12px",display:"flex",alignItems:"center",justifyContent:"center",
            background:drag?"rgba(0,255,0,0.04)":"#fff",transition:"all 0.15s"}}>
          <span style={{fontSize:12,color:"#aaa",textAlign:"center"}}>
            Fichier Excel Deepki (pas jour ou heure)<br/>Glisser ici ou cliquer
          </span>
          <input ref={ref} type="file" accept={ACCEPTED} style={{display:"none"}}
            onChange={e=>{const f=e.target.files?.[0];if(f)handleFile(f);}}/>
        </div>
      )}

      {filename && (
        <div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <span style={{fontSize:12,color:"var(--text-muted)"}}>📄 {filename}</span>
            <button onClick={reset} className="btn btn-ghost btn-sm" style={{fontSize:11,padding:"2px 8px"}}>✕ Changer de fichier</button>
          </div>

          {validating && <div style={{fontSize:12,color:"var(--text-faint)"}}>Vérification du fichier…</div>}

          {validateErr && (
            <div style={{background:"#fdecea",border:"1px solid #f5c6cb",borderRadius:6,padding:"10px 14px",fontSize:12,color:"var(--red)"}}>
              ⚠ {validateErr}
            </div>
          )}

          {report && (
            <div style={{background:"#fff",border:"1px solid #e0e0e0",borderRadius:6,padding:"10px 14px",fontSize:12}}>
              <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:report.gapsCount>0||report.warnings.length>0?8:0}}>
                <span>Pas : <strong>{report.pas}</strong></span>
                <span>Période : <strong>{fmtDate(report.tsFrom)} → {fmtDate(report.tsTo)}</strong></span>
                <span>{report.rowCount} lignes</span>
                {report.gapsCount > 0
                  ? <span style={{color:"#c8740a"}}>⚠ {report.gapsCount} trou{report.gapsCount>1?"s":""}</span>
                  : <span style={{color:"var(--green-dim)"}}>✓ Aucun trou</span>}
              </div>
              {report.gapsCount > 0 && (() => {
                const groups = groupConsecutiveGaps(report.gapsPreview, report.pas);
                const shown = groups.slice(0, 15);
                return (
                  <div style={{fontSize:11,marginBottom:report.warnings.length?8:0}}>
                    <span style={{color:"#c8740a"}}>{report.pas === "heure" ? "Heures manquantes" : "Dates manquantes"} : </span>
                    {shown.map((group, i, arr) => {
                      const { text, dst } = fmtGapGroup(group, report.pas);
                      return (
                        <span key={group[0]} style={{color: dst ? "#2196f3" : "#c8740a"}}>
                          {text}{i < arr.length - 1 ? ", " : ""}
                        </span>
                      );
                    })}
                    {groups.length > shown.length ? ` … (+${groups.length - shown.length} groupes)` : ""}
                    {report.gapsCount > report.gapsPreview.length ? " …" : ""}
                  </div>
                );
              })()}
              {report.warnings.length > 0 && (
                <div style={{color:"var(--text-faint)",fontSize:11}}>
                  {report.warnings.slice(0,5).map((w,i)=><div key={i}>• {w}</div>)}
                  {report.warnings.length > 5 && <div>… (+{report.warnings.length - 5} autres)</div>}
                </div>
              )}
            </div>
          )}

          {ingestErr && (
            <div style={{background:"#fdecea",border:"1px solid #f5c6cb",borderRadius:6,padding:"10px 14px",fontSize:12,color:"var(--red)",marginTop:8}}>
              ⚠ {ingestErr}
            </div>
          )}

          {report && !validateErr && (
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:10}}>
              <button onClick={handleConfirm} className="btn btn-black btn-sm">
                Confirmer l'intégration
              </button>
            </div>
          )}
        </div>
      )}

      {/* Même overlay plein écran que l'export PDF (ReportViewer.tsx) — bloque toute interaction pendant l'ingestion */}
      {ingestingDelayed && <LoadingScreen message="Import Deepki en cours…" overlay/>}
    </div>
  );
}
